import { Slot as SlotPrimitive } from "radix-ui"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/common/lib/utils"
import { getButtonSizes } from "./button-config"

// Standalone demo — no multi-client config, always the default sizes.
const buttonSizes = getButtonSizes("default")

const buttonVariants = cva(
    "cursor-pointer inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive",
    {
        variants: {
            variant: {
                default:
                    "primary-button bg-primary text-primary-foreground shadow-xs hover:bg-primary-subtle5 disabled:opacity-100 disabled:bg-neutral-subtle4 disabled:text-primary-foreground",
                destructive:
                    "bg-destructive text-white shadow-xs hover:bg-destructive/90 focus-visible:ring-destructive/20 dark:focus-visible:ring-destructive/40 dark:bg-destructive/60",
                destructiveinvert: "bg-destructive/50 text-destructive-foreground shadow-xs hover:bg-destructive",
                destructiveSecondary: "border-error border text-error bg-error-subtle1",
                outline:
                    "outline-button border-[1.5px] border-primary bg-transparent shadow-xs hover:bg-accent hover:text-accent-foreground dark:bg-transparent dark:border-input dark:hover:bg-input/50",
                secondary: "bg-secondary text-secondary-foreground shadow-xs hover:bg-secondary/80 button-secondary",
                ghost: "ghost-button hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50",
                link: "text-primary underline-offset-4 hover:underline",
                // Quaternary: the lowest-emphasis LABELLED button in the design
                // system — no background in any state, brand-green label
                // darkening one step on hover (`--primary-hover`, #0D3228 on
                // Migros Bank). `hover:bg-transparent` is stated rather than
                // omitted: `ghost` is what this replaces at call sites, and its
                // `hover:bg-accent` tint block is exactly what the design
                // removes, so "no background" belongs IN the variant instead of
                // as an override each consumer has to remember.
                quaternary:
                    "quaternary-button bg-transparent text-primary hover:bg-transparent hover:text-primary-hover",
                outlineSecondary:
                    "border-2 border-input bg-background shadow-xs hover:bg-accent hover:text-accent-foreground dark:border-input dark:bg-foreground dark:text-white dark:hover:bg-accent dark:hover:text-primary",
                questionDropdown:
                    "text-xs !py-1 min-w-[85px] !h-[2rem] transition-colors bg-mb-grey text-primary rounded-full px-4 duration-200 dark:border-2 border-primary dark:text-white border-main-border dark:text-primary",
                questionDropdownSecondary:
                    "text-xs !py-1 min-w-[85px] !h-[2rem] transition-colors bg-transparent text-font-primary border-main-border border rounded-full px-4 duration-200 dark:border-2 dark:text-white",
                sendQuestion:
                    "p-2 rounded-full text-primary focus:outline-hidden bg-mb-grey w-8 h-8 dark:text-white dark:hover:text-primary",
                answerOption:
                    "p-1 rounded-full transition-colors hover:bg-transparent text-primary-sub hover:text-primary",
            },
            size: {
                default: buttonSizes.default,
                sm: buttonSizes.sm,
                lg: buttonSizes.lg,
                icon: buttonSizes.icon,
                xl: buttonSizes.xl,
            },
        },
        defaultVariants: {
            variant: "default",
            size: "default",
        },
    },
)

function Button({
    className,
    variant,
    size,
    asChild = false,
    ...props
}: React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
        asChild?: boolean
    }) {
    const Comp = asChild ? SlotPrimitive.Slot : "button"

    return (
        <Comp data-slot="button" className={cn(buttonVariants({ variant, size, className }), "o-button")} {...props} />
    )
}

export { Button, buttonVariants }
